// Status semantics for the Integrations page, per
// docs/superpowers/specs/2026-08-04-integration-status-design.md.
//
// `ResolvedStatus` mirrors the batched GraphQL contract the backend will ship
// (status + machine-readable reason + detail). Until then, `adaptRestStatus`
// fills it client-side from the per-integration REST status payloads.

export type IntegrationBadgeStatus = `Active` | `In Progress` | `Broken` | `Deleting`

export type StatusReason =
  | `healthy` // backend reported Active
  | `assumed_configured` // config-based integration; row exists, no failure signal
  | `backfill_running` // backfill tasks exist and are under 100%
  | `backfill_queued` // sync integration configured but backfill has not started
  | `sync_stale` // backfill complete but the last sync is too old
  | `error_reported` // backend reported Broken for a config-based integration
  | `config_incomplete` // backend reported an empty status (not configured)
  | `deleting` // teardown in progress
  | `auth_failed` // backend reported an authentication failure
  | `backfill_stalled` // backfill was expected to start but hasn't

export type StatusDetail = {
  completion?: number
  patientsCompletion?: number
  appointmentsCompletion?: number
  transactionsCompletion?: number
  remainingPatients?: number
  remainingAppointments?: number
  remainingTransactions?: number
  remainingCalls?: number
  remainingForms?: number
  remainingSpendRecords?: number
  configuredPages?: number
}

export type ResolvedStatus = {
  status: IntegrationBadgeStatus
  reason: StatusReason
  detail: StatusDetail
}

/**
 * Raw payload from the status endpoints. `unsupported` marks the endpoint's
 * HTTP 400 for integration types it does not handle (DNI, Ascend, ElevenLabs);
 * `undefined` from the fetcher means the request failed (auth, 5xx, network) —
 * which is NOT evidence about the integration's health.
 */
export type RawIntegrationStatus = { status?: string; data?: unknown; unsupported?: true }

// Sync-pipeline integrations backfill then sync continuously; everything else
// is config-based (healthy when configured — silence is OK). Unknown slugs
// default to config: the row only exists because something is configured.
// RingCentral is config-based: its backfill is a manually-run bin script and
// its In Progress payload never carries a completion percentage.
const SYNC_SLUGS = new Set([`sikka`, `kolla`, `carestack`, `callrail`, `google`, `jotform`, `facebook-ads`])

export function integrationArchetype(slug: string): `sync` | `config` {
  return SYNC_SLUGS.has(slug) ? `sync` : `config`
}

const COMPLETION_LABELS: [keyof StatusDetail, string][] = [
  [`patientsCompletion`, `Patients`],
  [`appointmentsCompletion`, `Appointments`],
  [`transactionsCompletion`, `Transactions`],
  [`completion`, `Records`],
]

const REMAINING_LABELS: [keyof StatusDetail, string][] = [
  [`remainingPatients`, `Patients`],
  [`remainingAppointments`, `Appointments`],
  [`remainingTransactions`, `Transactions`],
  [`remainingCalls`, `Calls`],
  [`remainingForms`, `Forms`],
  [`remainingSpendRecords`, `Spend Records`],
  [`configuredPages`, `Configured Pages`],
]

function hasCompletion(detail: StatusDetail): boolean {
  return COMPLETION_LABELS.some(([key]) => Number.isFinite(detail[key]))
}

export function adaptRestStatus(
  slug: string,
  raw: RawIntegrationStatus | null | undefined,
): ResolvedStatus | undefined {
  // `null`/`undefined` means the fetch itself failed (transport, auth, 5xx) —
  // that's not evidence about the integration's health, so neither archetype
  // gets a status out of it.
  if (raw == null) return undefined

  const archetype = integrationArchetype(slug)
  const detail = (raw.data as StatusDetail) ?? {}

  if (raw.unsupported === true || raw.status === undefined) {
    // Endpoint explicitly doesn't support this integration type (its own HTTP
    // 400 — DNI, Ascend, ElevenLabs), or a payload with no `status` key.
    if (archetype === `config`) return { status: `Active`, reason: `assumed_configured`, detail }
    return undefined
  }

  switch (raw.status) {
    case `Active`:
      return { status: `Active`, reason: `healthy`, detail }
    case `Deleting`:
      return { status: `Deleting`, reason: `deleting`, detail }
    case `In Progress`:
      if (hasCompletion(detail)) return { status: `In Progress`, reason: `backfill_running`, detail }
      // No completion numbers means no backfill tasks exist. For config-based
      // integrations that is the normal state (their backfill is optional);
      // for sync integrations the backfill has not started yet.
      // ClickFunnels is config-based but has a real "never received a webhook"
      // state that must not read as Active, so it stays In Progress like a
      // sync integration until the backend reports completion/Active.
      if (archetype === `config` && slug !== `clickfunnels`) {
        return { status: `Active`, reason: `assumed_configured`, detail }
      }
      return { status: `In Progress`, reason: `backfill_queued`, detail }
    case `Broken`:
      return { status: `Broken`, reason: archetype === `sync` ? `sync_stale` : `error_reported`, detail }
    case ``:
      return { status: `Broken`, reason: `config_incomplete`, detail }
    default:
      // An unrecognized status string is not a positive health signal for any
      // integration. ClickFunnels must not fall through to the config
      // archetype's "assume configured" default here — its real state
      // (never-received webhook) already stays out of Active above, so an
      // unknown value must not undo that by resolving to Active.
      if (archetype === `config` && slug !== `clickfunnels`) {
        return { status: `Active`, reason: `assumed_configured`, detail }
      }
      return undefined
  }
}

const numberFormat = new Intl.NumberFormat(`en-US`)

function remainingLines(detail: StatusDetail): string[] {
  return REMAINING_LABELS.filter(([key]) => typeof detail[key] === `number` && detail[key]! > 0).map(
    ([key, label]) => `${label}: ${numberFormat.format(detail[key]!)}`,
  )
}

export function statusTooltipLines(resolved: ResolvedStatus): string[] {
  const { reason, detail } = resolved
  switch (reason) {
    case `assumed_configured`:
      return [`Configured. No failure signals.`, ...remainingLines(detail)]
    case `healthy`: {
      const lines = remainingLines(detail)
      return lines.length ? lines : [`Up to date`]
    }
    case `backfill_running`: {
      const lines = COMPLETION_LABELS.filter(([key]) => Number.isFinite(detail[key])).map(
        ([key, label]) => `${label}: ${detail[key]}% loaded`,
      )
      return lines.length ? lines : [`Initial data load running`]
    }
    case `backfill_queued`:
      return [`Waiting for initial data load`]
    case `sync_stale`:
      return [`Has not synced recently`]
    case `config_incomplete`:
      return [`Setup is incomplete`]
    case `deleting`:
      return [`Removing integration data…`, ...remainingLines(detail)]
    case `error_reported`:
      return [`Reported broken by the server`]
    case `auth_failed`:
      return [`Authentication failed`]
    case `backfill_stalled`:
      return [`Initial data load never started`]
    default: {
      // Exhaustiveness guard: if a new StatusReason member is added without a
      // case above, this line fails to compile. At runtime it also protects
      // against reasons the server sends that this build doesn't know about
      // yet, so a tooltip never throws on an unrecognized value.
      const exhaustive: never = reason
      return [`Status unavailable`]
    }
  }
}

export const STATUS_BADGE_CLASS: Record<IntegrationBadgeStatus, string> = {
  Active: `bg-green-200 text-green-600`,
  'In Progress': `bg-green-100 text-green-500`,
  Broken: `bg-amber-200 text-amber-600`,
  Deleting: `bg-red-200 text-red-600`,
}
